import { Button, Checkbox, Modal, TextInput } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { formatDateInputs, onlyNumberInput } from '../../Utils';
import { WarrantyPartsReplaced } from '../../Models/WarrantyPartsReplaced';
import {
  WARRANTY_PART_DESCRIPTION_MAX_LENGTH,
  WARRANTY_PART_INITIALS_MAX_LENGTH,
  WARRANTY_PART_PART_NUMBER_MAX_LENGTH,
  truncateToMaxLength,
} from '../../Constants/warrantyFieldLimits';

export default function EditWarrantyPartModal({
  show,
  setShow,
  warrantyPart,
  addUpdateWarrantyPart,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  warrantyPart: WarrantyPartsReplaced | null;
  addUpdateWarrantyPart: (part: WarrantyPartsReplaced) => void;
}) {
  const [formData, setFormData] = useState<WarrantyPartsReplaced>({
    id: 0,
    quantity: 1,
    partNumber: '',
    description: null,
    warrantyId: 0,
    amount: 0,
    initials: null,
    returnRequired: null,
    dateApproved: null,
  });

  const handleSetInitialValues = () => {
    const values = warrantyPart
      ? {
          ...warrantyPart,
        }
      : {
          id: 0,
          quantity: 1,
          partNumber: '',
          description: null,
          warrantyId: 0,
          amount: 0,
          initials: null,
          returnRequired: null,
          dateApproved: null,
        };
    setFormData(values as WarrantyPartsReplaced);
  };

  useEffect(() => {
    handleSetInitialValues();
  }, [warrantyPart, show]);

  const handleChangeCheckboxInputParts = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: checked }));
  };
  const handleChangeInputParts = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let next = value;
    if (name === 'partNumber') {
      next = truncateToMaxLength(WARRANTY_PART_PART_NUMBER_MAX_LENGTH, value);
    } else if (name === 'description') {
      next = truncateToMaxLength(WARRANTY_PART_DESCRIPTION_MAX_LENGTH, value);
    } else if (name === 'initials') {
      next = truncateToMaxLength(WARRANTY_PART_INITIALS_MAX_LENGTH, value);
    }
    setFormData((prevData) => ({ ...prevData, [name]: next }));
  };

  const onSubmit = async () => {
    if (!formData.partNumber) {
      toast.error('Unable to add item. Missing Part Number.');
    } else {
      addUpdateWarrantyPart(formData);
    }
  };

  function onCloseModal() {
    setShow(false);
  }

  return (
    <Modal show={show} size='4xl' onClose={onCloseModal} popup>
      <Modal.Header></Modal.Header>
      <Modal.Body>
        <div className='w-full'>
          <div className='w-full'>
            <div className='mx-1 flex items-end justify-center'>
              <div className='flex flex-1 justify-center'>
                <h1 className='text-gray-900 font-bold text-4xl'>
                  {warrantyPart === null ? 'New' : 'Edit'} Part
                </h1>
              </div>
            </div>
          </div>
          <div>
            <div className='container mx-auto p-2'>
              <div className='md:flex no-wrap md:-mx-2 '>
                <div className='w-full'>
                  <div className='bg-white'>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <form>
                      <div className='flex flex-col md:flex-none md:grid md:grid-cols-[50%_50%] text-xl'>
                        <div>
                          <div className='grid grid-cols-[45%_55%] px-0 text-sm lg:px-2 md:text-md lg:text-lg'>
                            <div className='flex'>
                              <div className='text-md font-bold content-center'>
                                Part Number:
                              </div>
                            </div>
                            <div className='w-full'>
                              <TextInput
                                id='partNumber'
                                name='partNumber'
                                type='text'
                                placeholder='Part Number...'
                                onChange={handleChangeInputParts}
                                value={formData.partNumber ?? ''}
                                sizing='md'
                                maxLength={WARRANTY_PART_PART_NUMBER_MAX_LENGTH}
                                required
                              />
                            </div>
                          </div>
                          <div className='grid grid-cols-[45%_55%] px-0 text-sm lg:px-2 md:text-md lg:text-lg'>
                            <div className='flex'>
                              <div className='text-md font-bold content-center'>
                                Description:
                              </div>
                            </div>
                            <div className='w-full'>
                              <TextInput
                                id='description'
                                name='description'
                                type='text'
                                placeholder='Description...'
                                onChange={handleChangeInputParts}
                                value={formData.description ?? ''}
                                sizing='md'
                                maxLength={WARRANTY_PART_DESCRIPTION_MAX_LENGTH}
                              />
                            </div>
                          </div>
                          <div className='grid grid-cols-[45%_55%] px-0 text-sm lg:px-2 md:text-md lg:text-lg'>
                            <div className='flex'>
                              <div className='text-md font-bold content-center'>
                                Initials:
                              </div>
                            </div>
                            <div className='w-full'>
                              <TextInput
                                id='initials'
                                name='initials'
                                type='text'
                                placeholder='Initials...'
                                onChange={handleChangeInputParts}
                                value={formData.initials ?? ''}
                                sizing='md'
                                maxLength={WARRANTY_PART_INITIALS_MAX_LENGTH}
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className='grid grid-cols-[45%_55%] px-0 text-sm lg:px-2 md:text-md lg:text-lg'>
                            <div className='flex'>
                              <div className='text-md font-bold content-center'>
                                Quantity:
                              </div>
                            </div>
                            <div className='w-full'>
                              <TextInput
                                id='quantity'
                                name='quantity'
                                type='text'
                                placeholder='Quantity...'
                                onChange={handleChangeInputParts}
                                value={formData.quantity ?? ''}
                                sizing='md'
                                onKeyDown={(event) => {
                                  if (onlyNumberInput(event)) {
                                    event.preventDefault();
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className='grid grid-cols-[45%_55%] px-0 text-sm lg:px-2 md:text-md lg:text-lg'>
                            <div className='flex'>
                              <div className='text-md font-bold content-center'>
                                Amount:
                              </div>
                            </div>
                            <div className='w-full'>
                              <TextInput
                                id='amount'
                                name='amount'
                                type='text'
                                placeholder='Amount...'
                                onChange={handleChangeInputParts}
                                value={formData.amount ?? ''}
                                sizing='md'
                              />
                            </div>
                          </div>
                          <div className='grid grid-cols-[45%_55%] px-0 text-sm lg:px-2 md:text-md lg:text-lg'>
                            <div className='flex'>
                              <div className='text-md font-bold content-center'>
                                Return Required:
                              </div>
                            </div>
                            <div className='w-full h-[42px]'>
                              <Checkbox
                                id='returnRequired'
                                name='returnRequired'
                                onChange={handleChangeCheckboxInputParts}
                                checked={formData.returnRequired ?? false}
                              />
                            </div>
                          </div>
                          <div className='grid grid-cols-[45%_55%] px-0 text-sm lg:px-2 md:text-md lg:text-lg'>
                            <div className='flex'>
                              <div className='text-md font-bold content-center'>
                                Date Approved:
                              </div>
                            </div>
                            <div className='w-full'>
                              <TextInput
                                id='dateApproved'
                                name='dateApproved'
                                type='date'
                                placeholder='Date...'
                                onChange={handleChangeInputParts}
                                value={formatDateInputs(
                                  formData.dateApproved ?? ''
                                )}
                                sizing='md'
                                pattern='\d{4}-\d{2}-\d{2}'
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='flex justify-end pt-2'>
                        <Button
                          type='button'
                          className='buttonMain'
                          onClick={() => onSubmit()}
                        >
                          Save
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
